import { QueryKey } from '@crm/api'
import { getColor } from '@crm/components/Charts/utils'
import { abbreviateNumber } from '@crm/utils'
import { Options, TDataShape } from '@hey-api/client-axios'
import { useLocales } from '@shared/locales'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { Card, Empty } from 'antd'
import { useMemo } from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

const color = getColor(4)

type Props<TData extends TDataShape, TRow> = {
  label: string
  getQueryFunction: (
    options?: TData
  ) => UseQueryOptions<TRow[], Error, TRow[], QueryKey<Options<TData>>>
  baseOptions?: TData
}

type Point = { value: number; date: string }

export const LineChart = <
  TData extends TDataShape,
  TRow extends {
    date: string
    value: number
  }
>({
  label,
  getQueryFunction,
  baseOptions
}: Props<TData, TRow>) => {
  const { t } = useLocales()

  const mergedOptions = useMemo<TData>(
    () => baseOptions ?? ({} as TData),
    [baseOptions]
  )

  const { data = [], isLoading } = useQuery({
    ...getQueryFunction(mergedOptions),
    select: (rows: TRow[]): Point[] =>
      rows.map(({ value, date }) => ({
        value,
        date
      }))
  })

  return (
    <Card title={label} loading={isLoading}>
      {data.length ? (
        <div className="chart__container">
          <ResponsiveContainer>
            <ComposedChart data={data}>
              <defs>
                <linearGradient id="colorLine4" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="4 0" />
              <XAxis dataKey="date" scale="band" />
              <YAxis
                yAxisId="left"
                orientation="left"
                scale="auto"
                type="number"
                tickFormatter={(v) => abbreviateNumber(Number(v))}
              />
              <Tooltip
                content={({ active, payload }) =>
                  active && payload
                    ? `${payload[0].payload.date} - ${payload[0].value}`
                    : null
                }
              />
              <Area
                type="monotone"
                yAxisId="left"
                dataKey="value"
                stroke={color}
                fillOpacity={1}
                fill="url(#colorLine4)"
                activeDot={{ stroke: color, strokeWidth: 4 }}
                dot={{ stroke: 'white', strokeWidth: 3, r: 6, fill: color }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Empty description={t('main.no_data')} />
      )}
    </Card>
  )
}
